import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, Calendar, Clock, AlertTriangle, TrendingUp, Target, ArrowRight, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import RecoveryPlanService from '../../services/RecoveryPlanService';

const styles = `
  .task-card {
    transition: all 0.3s ease;
  }
  .task-card:hover {
    transform: translateX(5px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  .arrow-right {
    animation: slide 1s ease-in-out infinite;
  }
  .plan-modified {
    animation: pulse-azure 2s ease-in-out;
  }
  @keyframes slide {
    0%, 100% { transform: translateX(0); }
    50% { transform: translateX(10px); }
  }
  @keyframes pulse-azure {
    0%, 100% { background-color: rgb(239 246 255); }
    50% { background-color: rgb(219 234 254); }
  }
  .schedule-day {
    transition: all 0.2s ease;
  }
  .schedule-day:hover {
    background-color: rgb(249 250 251);
  }
  @media (max-width: 768px) {
    .desktop-flow {
      display: none;
    }
    .mobile-stack {
      display: block;
    }
  }
  @media (min-width: 769px) {
    .desktop-flow {
      display: flex;
    }
    .mobile-stack {
      display: none;
    }
  }
`;

/**
 * Recovery Plan Preview Component
 * Shows a detailed, actionable recovery plan for overdue tasks
 * Maximally Assistive: System creates the entire plan for the user
 */
const RecoveryPlanPreview = ({ overdueTasks, upcomingTasks, onApply, onClose }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  // Removed unused state: selectedPhase, setSelectedPhase
  const [modifiedPlan, setModifiedPlan] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [taskToMove, setTaskToMove] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const generatePlan = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const recoveryPlan = RecoveryPlanService.createRecoveryPlan(
        overdueTasks,
        upcomingTasks,
        { aggressive: false }
      );
      setPlan(recoveryPlan);
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      console.error('Error generating recovery plan:', error);
      setError({
        type: 'generation',
        message: error.message || 'Failed to generate recovery plan. Please try again.',
        canRetry: true
      });
    } finally {
      setLoading(false);
    }
  }, [overdueTasks, upcomingTasks]); // Dependencies for useCallback

  useEffect(() => {
    // Only generate plan on mount or if tasks significantly change
    if (overdueTasks && overdueTasks.length > 0) {
      generatePlan();
    }
  }, [overdueTasks?.length, generatePlan]); // Only re-run if the number of tasks changes

  const retryGeneratePlan = () => {
    if (retryCount < 3) { // Limit retries
      setRetryCount(prev => prev + 1);
      generatePlan();
    } else {
      setError({
        type: 'generation',
        message: 'Unable to generate recovery plan after multiple attempts. Please check your data and try again later.',
        canRetry: false
      });
    }
  };

  const handleApplyPlan = async () => {
    const finalPlan = modifiedPlan || plan;

    if (!finalPlan || !finalPlan.redistributedTasks || finalPlan.redistributedTasks.length === 0) {
      setError({
        type: 'application',
        message: 'No tasks to reschedule. Please check your recovery plan.',
        canRetry: false
      });
      return;
    }

    setApplying(true);
    setError(null);

    try {
      await onApply(finalPlan);
      onClose();
    } catch (error) {
      console.error('Error applying recovery plan:', error);
      setError({
        type: 'application',
        message: error.message || 'Failed to apply recovery plan. Your tasks were not modified. Please try again.',
        canRetry: true
      });
    } finally {
      setApplying(false);
    }
  };

  const handleMoveTask = (task, currentDate) => {
    setTaskToMove({ task, currentDate });
    setShowMoveModal(true);
  };

  const handleRemoveTask = (taskToRemove, currentDate) => {
    const currentPlan = modifiedPlan || plan;
    const newSchedule = { ...currentPlan.dailySchedule };

    // Remove task from current date
    if (newSchedule[currentDate]) {
      newSchedule[currentDate] = {
        ...newSchedule[currentDate],
        tasks: newSchedule[currentDate].tasks.filter(t =>
          !(t.id === taskToRemove.id || t.title === taskToRemove.title)
        ),
        totalHours: newSchedule[currentDate].totalHours - (taskToRemove.estimated_hours || 1)
      };
    }

    // Remove from redistributed tasks
    const newRedistributedTasks = currentPlan.redistributedTasks.filter(t =>
      !(t.id === taskToRemove.id || t.title === taskToRemove.title)
    );

    const updatedPlan = {
      ...currentPlan,
      dailySchedule: newSchedule,
      redistributedTasks: newRedistributedTasks,
      summary: {
        ...currentPlan.summary,
        totalOverdue: newRedistributedTasks.length
      }
    };

    setModifiedPlan(updatedPlan);
  };

  const handleMoveToDay = (targetDate) => {
    if (!taskToMove) return;

    const currentPlan = modifiedPlan || plan;
    const newSchedule = { ...currentPlan.dailySchedule };
    const { task, currentDate } = taskToMove;

    // Remove from current date
    if (newSchedule[currentDate]) {
      newSchedule[currentDate] = {
        ...newSchedule[currentDate],
        tasks: newSchedule[currentDate].tasks.filter(t =>
          !(t.id === task.id || t.title === task.title)
        ),
        totalHours: newSchedule[currentDate].totalHours - (task.estimated_hours || 1)
      };
    }

    // Add to target date
    if (!newSchedule[targetDate]) {
      newSchedule[targetDate] = {
        date: new Date(targetDate),
        tasks: [],
        existingTasks: [],
        totalHours: 0,
        isWeekend: false
      };
    }

    const movedTask = {
      ...task,
      scheduled_date: targetDate,
      reason: `Manually moved from ${format(new Date(currentDate), 'MMM d')}`
    };

    newSchedule[targetDate].tasks.push(movedTask);
    newSchedule[targetDate].totalHours += (task.estimated_hours || 1);

    // Update redistributed tasks
    const newRedistributedTasks = currentPlan.redistributedTasks.map(t =>
      (t.id === task.id || t.title === task.title)
        ? { ...t, scheduled_date: targetDate }
        : t
    );

    const updatedPlan = {
      ...currentPlan,
      dailySchedule: newSchedule,
      redistributedTasks: newRedistributedTasks
    };

    setModifiedPlan(updatedPlan);
    setShowMoveModal(false);
    setTaskToMove(null);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-azure-600 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-lg font-medium text-slate-900">Creating your recovery plan...</p>
            <p className="text-sm text-slate-600 mt-2">Analyzing workload and optimizing schedule</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md">
          <div className="flex flex-col items-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Recovery Plan Error</h3>
            <p className="text-sm text-gray-600 text-center mb-4">{error.message}</p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              {error.canRetry && (
                <button
                  onClick={error.type === 'generation' ? retryGeneratePlan : handleApplyPlan}
                  className="px-4 py-2 text-sm bg-azure-600 text-white rounded-lg hover:bg-azure-700"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return null;
  }

  // Use modified plan if available, otherwise use original plan
  const displayPlan = modifiedPlan || plan;

  return (
    <>
      <style>{styles}</style>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-50 rounded-2xl shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        {/* Header with Clear Value Prop */}
        <div className="bg-white rounded-t-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900">Your Recovery Plan</h1>
                {modifiedPlan && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-azure-100 text-azure-700 rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    Modified
                  </span>
                )}
              </div>
              <p className="text-lg text-gray-600 mt-2">Let's get you back on track in <span className="font-semibold text-azure-600">{displayPlan.summary.daysToRecover} days</span></p>
              {modifiedPlan && (
                <p className="text-sm text-azure-600 mt-1">
                  You've customized this plan - changes will be applied when you confirm
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Visual Progress Bar */}
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Recovery Progress</span>
              <span className="text-sm text-gray-500">
                {plan.summary.totalOverdue - displayPlan.summary.totalOverdue} of {plan.summary.totalOverdue} tasks organized
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-azure-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${Math.max(10, (plan.summary.totalOverdue - displayPlan.summary.totalOverdue) / plan.summary.totalOverdue * 100)}%`
                }}
              ></div>
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-xs text-red-600 font-medium">{displayPlan.summary.totalOverdue} Remaining</span>
              <span className="text-xs text-azure-600">Organizing Tasks</span>
              <span className="text-xs text-emerald-600 font-medium">Ready to Apply!</span>
            </div>
          </div>
        </div>

        {/* Main Content: Before/After Split View */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

            {/* Left: Current Situation */}
            <div className="bg-white rounded-2xl shadow-lg p-6 schedule-day">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-red-600 mb-1">Current Situation</h2>
                <p className="text-sm text-gray-600">Your overdue tasks sorted by priority</p>
              </div>

              <div className="space-y-3">
                {overdueTasks.map((task, index) => {
                  const priority = task.priority || 'normal';
                  const priorityConfig = {
                    critical: { color: 'red', bg: 'red-50', border: 'red-500' },
                    high: { color: 'orange', bg: 'orange-50', border: 'orange-500' },
                    normal: { color: 'yellow', bg: 'yellow-50', border: 'yellow-500' },
                    low: { color: 'gray', bg: 'gray-50', border: 'gray-500' }
                  };
                  const config = priorityConfig[priority] || priorityConfig.normal;

                  const daysLate = Math.ceil((new Date() - new Date(task.due_date)) / (1000 * 60 * 60 * 24));

                  return (
                    <div key={index} className={`task-card border-l-4 border-${config.border} bg-${config.bg} rounded-lg p-4`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{task.title}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <span className={`text-xs bg-${config.color}-100 text-${config.color}-700 px-2 py-1 rounded capitalize`}>
                              {priority}
                            </span>
                            <span className="text-xs text-gray-600">{task.category}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500 line-through">
                            Due {format(parseISO(task.due_date), 'MMM d')}
                          </div>
                          <div className={`text-sm font-bold text-${config.color}-600`}>
                            {daysLate} days late
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

           

            {/* Right: Recovery Schedule */}
            <div className="bg-white rounded-2xl shadow-lg p-6 schedule-day">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-emerald-600 mb-1">Recovery Schedule</h2>
                <p className="text-sm text-gray-600">Smart redistribution across next {displayPlan.summary.daysToRecover} days</p>
              </div>

              {/* Daily Breakdown */}
              {Object.entries(displayPlan.dailySchedule).slice(0, 3).map(([dateKey, day], dayIndex) => (
                <div key={dateKey} className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${dayIndex === 0 ? 'text-azure-600' : 'text-gray-700'}`}>
                        {dayIndex === 0 ? `TODAY - ${format(parseISO(dateKey), 'MMM d')}` : dayIndex === 1 ? `TOMORROW - ${format(parseISO(dateKey), 'MMM d')}` : format(parseISO(dateKey), 'MMM d')}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        day.totalHours > 4 ? 'bg-amber-100 text-amber-700' :
                        dayIndex === 0 ? 'bg-azure-100 text-azure-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {day.totalHours.toFixed(1)} hours total
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {day.totalHours > 4 && (
                        <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Heavy
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {day.tasks.length + (day.existingTasks?.length || 0)} total tasks
                      </span>
                    </div>
                  </div>

                  <div className={`space-y-2 pl-4 border-l-2 ${dayIndex === 0 ? 'border-azure-300' : 'border-gray-300'}`}>
                    {/* Existing Tasks */}
                    {day.existingTasks && day.existingTasks.map((task, taskIndex) => (
                      <div key={taskIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-300">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-700">{task.title}</h4>
                            <p className="text-xs text-gray-500 mt-1">{task.description || 'Already scheduled'}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">{task.estimated_hours || 1}h</div>
                            <div className="text-xs text-gray-400">Fixed</div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Divider */}
                    {day.tasks.length > 0 && (
                      <div className="flex items-center gap-2 py-1">
                        <div className="flex-1 h-px bg-emerald-200"></div>
                        <span className="text-xs text-emerald-600 font-medium">+ Recovery Tasks</span>
                        <div className="flex-1 h-px bg-emerald-200"></div>
                      </div>
                    )}

                    {/* Recovery Tasks */}
                    {day.tasks.map((task, taskIndex) => (
                      <div key={taskIndex} className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{task.title}</h4>
                            <p className="text-xs text-emerald-600 mt-1">
                              ✓ {task.reason || 'Rescheduled for recovery'}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">{task.estimated_hours || 1}h</div>
                            <div className="flex gap-1 mt-1">
                              <button
                                onClick={() => handleMoveTask(task, dateKey)}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-azure-50 text-azure-600 hover:bg-azure-100 hover:text-azure-700 rounded border border-azure-200 transition-colors"
                                title="Move to different day"
                              >
                                <ArrowRight className="w-3 h-3" />
                                Move
                              </button>
                              <button
                                onClick={() => handleRemoveTask(task, dateKey)}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded border border-red-200 transition-colors"
                                title="Remove from recovery plan"
                              >
                                <Trash2 className="w-3 h-3" />
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Capacity Warning */}
                    {day.totalHours > 4 && (
                      <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span className="text-xs text-amber-700">Full day - consider moving some tasks to lighter days</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Week Overview */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 mb-2">Week at a Glance</h3>
                <div className="grid grid-cols-7 gap-1">
                  {Object.entries(displayPlan.dailySchedule).slice(0, 7).map(([dateKey, day]) => (
                    <div key={dateKey} className="text-center">
                      <div className="text-xs text-gray-500">{format(parseISO(dateKey), 'EEEEEE')}</div>
                      <div className={`mt-1 rounded flex items-end justify-center ${
                        day.totalHours > 4 ? 'h-12 bg-amber-200' :
                        day.totalHours > 2 ? 'h-10 bg-emerald-300' :
                        'h-8 bg-emerald-200'
                      }`}>
                        <div className={`text-xs font-bold ${
                          day.totalHours > 4 ? 'text-amber-700' : 'text-emerald-700'
                        }`}>
                          {day.totalHours.toFixed(1)}h
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="bg-white rounded-b-2xl shadow-lg p-6">
          {modifiedPlan && (
            <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 mb-4 plan-modified">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-azure-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-azure-900">Plan Customized</h4>
                  <p className="text-sm text-azure-700 mt-1">
                    You've made {plan.summary.totalOverdue - displayPlan.summary.totalOverdue} change(s) to the recovery plan.
                    {displayPlan.summary.totalOverdue === 0
                      ? " All overdue tasks have been organized!"
                      : ` ${displayPlan.summary.totalOverdue} task(s) remaining to organize.`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">
                {displayPlan.summary.totalOverdue === 0 ? "Plan Complete!" : "Ready to get back on track?"}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {displayPlan.summary.totalOverdue === 0
                  ? "All tasks have been organized. Apply this plan to update your schedule."
                  : `This plan will clear ${displayPlan.summary.totalOverdue} overdue tasks by ${format(new Date(Date.now() + displayPlan.summary.daysToRecover * 24 * 60 * 60 * 1000), 'MMM d')}`
                }
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {modifiedPlan ? "Continue Editing" : "Customize Plan"}
              </button>
              <button
                onClick={() => setShowConfirmation(true)}
                disabled={applying}
                className={`px-6 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                  displayPlan.summary.totalOverdue === 0
                    ? "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                    : "bg-gradient-to-r from-azure-600 to-azure-700 hover:from-azure-700 hover:to-azure-800"
                } text-white disabled:opacity-50`}
              >
                {applying ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Applying Plan...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4" />
                    {displayPlan.summary.totalOverdue === 0 ? "Apply Complete Plan" : "Apply Recovery Plan"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        </div>

        {/* Move Task Modal */}
        {showMoveModal && taskToMove && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Move Task</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Move "{taskToMove.task.title}" to a different day
                </p>
              </div>

              <div className="space-y-2 max-h-60 overflow-y-auto">
                {Object.entries((modifiedPlan || plan).dailySchedule).map(([dateKey, day]) => {
                  const isCurrentDate = dateKey === taskToMove.currentDate;
                  const willBeOverloaded = day.totalHours + (taskToMove.task.estimated_hours || 1) > 4;

                  return (
                    <button
                      key={dateKey}
                      onClick={() => handleMoveToDay(dateKey)}
                      disabled={isCurrentDate}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        isCurrentDate
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : willBeOverloaded
                          ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-azure-50 hover:border-azure-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {format(new Date(dateKey), 'EEEE, MMM d')}
                          </div>
                          <div className="text-xs text-gray-500">
                            {day.tasks.length} tasks, {day.totalHours.toFixed(1)}h scheduled
                          </div>
                        </div>
                        <div className="text-right">
                          {willBeOverloaded && (
                            <span className="text-xs text-amber-600 font-medium">
                              Will be {(day.totalHours + (taskToMove.task.estimated_hours || 1)).toFixed(1)}h
                            </span>
                          )}
                          {isCurrentDate && (
                            <span className="text-xs text-gray-400">Current</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowMoveModal(false);
                    setTaskToMove(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Recovery Plan</h3>
                <p className="text-sm text-gray-600 mt-2">
                  This will apply your recovery plan and update {displayPlan.redistributedTasks?.length || 0} task schedules.
                  {modifiedPlan && (
                    <span className="block mt-1 text-azure-600 font-medium">
                      Your customizations will be applied.
                    </span>
                  )}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">What will happen:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• {displayPlan.redistributedTasks?.length || 0} tasks will be rescheduled</li>
                  <li>• Due dates will be updated based on your recovery plan</li>
                  <li>• Your timeline will be automatically refreshed</li>
                  {displayPlan.summary.totalOverdue > 0 && (
                    <li className="text-amber-600">• {displayPlan.summary.totalOverdue} tasks will remain to organize manually</li>
                  )}
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowConfirmation(false);
                    handleApplyPlan();
                  }}
                  disabled={applying}
                  className="flex-1 px-4 py-2 text-sm bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 transition-colors"
                >
                  {applying ? 'Applying...' : 'Apply Recovery Plan'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RecoveryPlanPreview;